import { debug, warn } from './console';
import { recoverFromStaleBuildChunk } from './chunkLoadError';

/** Idle deadline before a warm is forced through anyway (ms). */
const WARM_IDLE_TIMEOUT_MS = 3000;
/** Delay used when requestIdleCallback is unavailable (Safari), ms. */
const WARM_FALLBACK_DELAY_MS = 2000;

/**
 * Defer-import mapbox-gl so the chunk is often cached before the user opens a map.
 * Skips when Save-Data is enabled. Returns a cancel function (e.g. on route unmount).
 */
export function warmMapboxChunk(): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } })
    .connection;
  if (connection?.saveData) {
    debug('[warmMapboxChunk] skipped: saveData');
    return () => {};
  }

  const run = () => {
    void import('mapbox-gl')
      .then(() => {
        debug('[warmMapboxChunk] mapbox-gl chunk preloaded');
      })
      .catch((err: unknown) => {
        warn('[warmMapboxChunk] preload import failed:', err);
      });
  };

  const ric = window.requestIdleCallback?.bind(window) as
    | ((cb: () => void, opts?: IdleRequestOptions) => number)
    | undefined;

  if (ric) {
    const id = ric(run, { timeout: WARM_IDLE_TIMEOUT_MS });
    return () => {
      window.cancelIdleCallback?.(id);
    };
  }

  const tid = window.setTimeout(run, WARM_FALLBACK_DELAY_MS);
  return () => clearTimeout(tid);
}

/**
 * Warm a set of lazy component loaders (import.meta.glob entries) so the FIRST click on a page does
 * not pay for its cold dynamic import.
 *
 * WHY THIS EXISTS. In live mode with the map playing, clicking another live page did nothing until
 * the user paused. Nothing holds a lock: the load path ends in a plain `import()`, and the live map
 * makes no requests while playing — it just keeps the main thread busy every frame (rAF playhead ->
 * GL layers, boat canvas, wind particles, tables, uPlot). A cold dev import is hundreds of separate
 * modules, each needing main-thread parse/eval before its own imports are even discovered, so it
 * barely advances. Pausing freed the thread; after that the page opened instantly because the module
 * registry was warm — which is what this does ahead of the click instead.
 *
 * This does not make the import faster. It makes the user not wait for it.
 *
 * One loader at a time: a parallel burst is a worse main-thread hit than the click it prevents. The
 * idle `timeout` guarantees progress even when the thread is saturated (the case this exists for).
 * Returns a cancel function — register it with `onCleanup`, never by returning it from an effect.
 */
export function warmComponentChunks(loaders: Array<() => Promise<unknown>>): () => void {
  if (typeof window === 'undefined' || loaders.length === 0) {
    return () => {};
  }

  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } })
    .connection;
  if (connection?.saveData) {
    debug('[warmComponentChunks] skipped: saveData');
    return () => {};
  }

  const ric = window.requestIdleCallback?.bind(window) as
    | ((cb: () => void, opts?: IdleRequestOptions) => number)
    | undefined;

  let cancelled = false;
  let idleId: number | null = null;
  // `number`, not ReturnType<typeof setTimeout>: this project's types resolve the bare `setTimeout`
  // to Node's (which returns Timeout), while `window.setTimeout` returns a number.
  let timerId: number | null = null;
  let index = 0;

  const schedule = () => {
    if (cancelled || index >= loaders.length) return;
    if (ric) {
      idleId = ric(run, { timeout: WARM_IDLE_TIMEOUT_MS });
    } else {
      timerId = window.setTimeout(run, WARM_FALLBACK_DELAY_MS);
    }
  };

  const run = () => {
    idleId = null;
    timerId = null;
    if (cancelled) return;
    // A background window must not spend main thread warming; wait until it is visible again.
    if (document.hidden) {
      schedule();
      return;
    }
    const loader = loaders[index];
    index += 1;
    if (!loader) {
      schedule();
      return;
    }
    void loader()
      .then(() => {
        if (!cancelled) debug('[warmComponentChunks] warmed', index, 'of', loaders.length);
      })
      // A warm miss costs nothing but a cold click later — the real load reports its own errors.
      // A *stale* chunk is the exception: the warmer is the earliest thing in the tab to notice a
      // deploy has landed underneath it, so recovering here means the user never meets the dead
      // click at all. Cancelled means the Sidebar is unmounting, and whatever navigation is doing
      // that will hit the same stale chunk and be caught by the global handler in index.tsx.
      .catch((err: unknown) => {
        if (!cancelled && recoverFromStaleBuildChunk(err, 'warmComponentChunks')) return;
        debug('[warmComponentChunks] warm failed (non-fatal):', err);
      })
      .finally(() => schedule());
  };

  schedule();

  return () => {
    cancelled = true;
    if (idleId != null) {
      window.cancelIdleCallback?.(idleId);
      idleId = null;
    }
    if (timerId != null) {
      window.clearTimeout(timerId);
      timerId = null;
    }
  };
}
